import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ordersApi } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, RefreshCw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface OrderData {
  clientName: string;
  totalItems: number;
  itemTotal: number;
  purchaseOrderId?: string;
  orderNumber?: string;
}

export default function AdminOrders() {
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await ordersApi.getTotalOrdersAmount();
      setOrders(response.data || []);
      toast({
        title: "Dados carregados",
        description: `${response.data?.length || 0} pedidos encontrados`,
      });
    } catch (error) {
      console.error("Erro ao buscar pedidos:", error);
      toast({
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar os dados dos pedidos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getTotalValue = () => {
    return orders.reduce((acc, order) => acc + order.itemTotal, 0);
  };

  const getTotalItems = () => {
    return orders.reduce((acc, order) => acc + order.totalItems, 0);
  };

  return (
    <Layout title="[ADMIN] Pedidos">
      <div className="space-y-6">
        <Card className="border-yellow-500 bg-yellow-50">
          <CardHeader className="bg-yellow-500 text-black">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-6 w-6" />
                <CardTitle className="text-xl">[ADMIN] Consolidado de Pedidos</CardTitle>
              </div>
              <Button
                onClick={fetchOrders}
                disabled={loading}
                variant="outline"
                size="sm"
                className="bg-white hover:bg-gray-100"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total de Escolas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{orders.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total de Itens
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{getTotalItems()}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Valor Total
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(getTotalValue())}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Nome da Escola</TableHead>
                    <TableHead className="text-center">Quantidade de Itens</TableHead>
                    <TableHead className="text-right">Valor Total (R$)</TableHead>
                    {orders.some(o => o.orderNumber) && (
                      <TableHead className="text-center">Nº do Pedido</TableHead>
                    )}
                    {orders.some(o => o.purchaseOrderId) && (
                      <TableHead className="text-center">ID do Pedido</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <div className="animate-pulse">Carregando dados...</div>
                      </TableCell>
                    </TableRow>
                  ) : orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        Nenhum pedido encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {order.clientName}
                        </TableCell>
                        <TableCell className="text-center">
                          {order.totalItems}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(order.itemTotal)}
                        </TableCell>
                        {orders.some(o => o.orderNumber) && (
                          <TableCell className="text-center">
                            {order.orderNumber || "-"}
                          </TableCell>
                        )}
                        {orders.some(o => o.purchaseOrderId) && (
                          <TableCell className="text-center">
                            {order.purchaseOrderId || "-"}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
